import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, ArrowRight, Search, Heart, User, Menu, X, SlidersHorizontal, PackageOpen, Grid2X2 } from 'lucide-react';
import { catalog, categories, categoriesOf, selectProducts, fetchAdProducts, money, formatDate, isNewArrival } from './catalog';
import { coupangLink } from './affiliate';
import noProductImage from '../images/No-product.webp';
import './storefront.css';

/**
 * 서버에 등록된 소개 상품을 한 번 불러온다.
 *
 * 예전에는 catalog.js 의 예시 12건(GUCCI·NIKE…)만 그렸다. 봇이 매일 상품을
 * 골라 넣어도 화면에는 영원히 그 12건이 떴다. 이제 실제 등록분을 쓰고,
 * 하나도 없을 때만 예시로 되돌아간다.
 */
function useAdProducts() {
  const [state, setState] = useState({ status: 'loading', products: [], disclosure: '' });
  useEffect(() => {
    let alive = true;
    fetchAdProducts()
      .then(({ products, disclosure }) => { if (alive) setState({ status: 'ready', products, disclosure }); })
      // 못 불러온 것과 등록이 없는 것은 사람이 할 일이 다르다. 구분해 둔다.
      .catch(() => { if (alive) setState({ status: 'error', products: [], disclosure: '' }); });
    return () => { alive = false; };
  }, []);
  return state;
}

function useSaved() {
  const [saved, setSaved] = useState(() => {
    try { const value = JSON.parse(localStorage.getItem('ants-market-saved') || '[]'); return Array.isArray(value) ? value.filter(Number.isInteger) : []; } catch (_) { return []; }
  });
  const toggle = id => setSaved(previous => {
    const next = previous.includes(id) ? previous.filter(x => x !== id) : [...previous, id];
    try { localStorage.setItem('ants-market-saved', JSON.stringify(next)); } catch (_) { /* device storage may be disabled */ }
    return next;
  });
  return [saved, toggle];
}

export function MarketHeader({ onMenuToggle, menuOpen }) {
  const location = useLocation();
  const history = useHistory();
  const [query, setQuery] = useState('');
  return <div className="am-header">
    <div className="am-topline">
      {onMenuToggle && <button className="am-icon am-menu" onClick={onMenuToggle} aria-label="카테고리 메뉴" aria-expanded={menuOpen}>{menuOpen ? <X /> : <Menu />}</button>}
      <Link to="/Shop" className="am-logo" aria-label="앤츠마켓 홈"><span className="am-monogram" aria-hidden="true">a<span>↗</span></span><span>ANTS<span className="am-logo-light">MARKET</span><small>FINDS · STORIES · TRENDS</small></span></Link>
      <form className="am-header-search" onSubmit={e => { e.preventDefault(); history.push(`/ShowItem?q=${encodeURIComponent(query)}`); }}>
        <Search size={19} /><input aria-label="전체 상품 검색" placeholder="어떤 취향을 찾고 있나요?" value={query} onChange={e => setQuery(e.target.value)} /><button aria-label="검색"><ArrowRight size={18} /></button>
      </form>
      <div className="am-actions"><Link to="/ShowItem?saved=1" aria-label="저장한 발견"><Heart size={21} /></Link><Link to="/mypage" aria-label="마이페이지"><User size={21} /></Link></div>
    </div>
    <nav className="am-nav" aria-label="쇼핑 메뉴">
      {[['/Shop', '발견'], ['/ShowItem', '상품 소개'], ['/Shop#journal', '트렌드 노트'], ['/ShowItem?category=의류', '패션'], ['/ShowItem?category=액세서리', '라이프스타일']].map(([url, title]) => <Link key={url} to={url} aria-current={`${location.pathname}${location.search}` === url ? 'page' : undefined}>{title}</Link>)}
      <Link className="am-nav-end" to="/Shop#affiliate-info">제휴 안내 <ArrowUpRight size={15} /></Link>
    </nav>
  </div>;
}

function ProductCard({ product, saved, toggle }) {
  const displayPrice = product.priceText || (product.price ? `${money(product.price)}원` : '');
  const isAffiliate = coupangLink(product.affiliateUrl);
  const formattedDate = formatDate(product.createdAt);
  const isNew = isNewArrival(product.createdAt);

  return <article className="am-product">
    <div className="am-product-image">
      <Link to={`/product/${product.id}`} tabIndex={-1} aria-hidden="true">
        <img
          src={product.image || noProductImage}
          alt={product.name}
          loading="lazy"
          width="400"
          height="460"
          onError={e => { if (e.target.src === noProductImage) return; e.target.onerror = null; e.target.src = noProductImage; }}
        />
      </Link>
      <button className="am-save" aria-label={`${product.name} 찜하기`} aria-pressed={saved.includes(product.id)} onClick={() => toggle(product.id)}>
        <Heart size={19} fill={saved.includes(product.id) ? 'currentColor' : 'none'} />
      </button>
      <div className="am-card-badges">
        {isNew && <span className="am-badge-new">NEW</span>}
        {product.trendSource && <span className="am-trend-tag">TREND</span>}
      </div>
    </div>
    <div className="am-product-copy">
      <div className="am-card-meta">
        <div className="am-card-meta-left">
          <span>{product.brand}</span>
          {product.isAgent && <small className="am-agent-tag">AI 큐레이션</small>}
        </div>
        {formattedDate && <span className="am-card-date">{formattedDate}</span>}
      </div>
      <Link to={`/product/${product.id}`}>
        <h3>{product.name}</h3>
      </Link>
      {displayPrice && <strong className="am-card-price">{displayPrice}</strong>}
      {product.trendSource && <p className="am-trend-source">{product.trendSource}</p>}
      <div className="am-card-author-bar">
        <span className="am-card-author">작성자 <strong>{product.author || (product.isAgent ? 'AI 큐레이터' : '관리자')}</strong></span>
        <span className="am-card-category">{product.category} · {isAffiliate ? '쿠팡 제휴' : '소개'}</span>
      </div>
      <div className="am-card-btn-row" style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
        <Link className="am-read-link" to={`/product/${product.id}`} style={{ flex: '1 1 auto', textAlign: 'center' }}>
          상세 노트 <ArrowUpRight size={14} />
        </Link>
        {product.affiliateUrl && (
          <a
            className="am-coupang-btn"
            href={product.affiliateUrl}
            target="_blank"
            rel="sponsored noopener noreferrer"
            style={{
              background: '#e1251b',
              color: '#ffffff',
              padding: '7px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '700',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              whiteSpace: 'nowrap'
            }}
          >
            쿠팡 구매 <ArrowUpRight size={13} />
          </a>
        )}
      </div>
    </div>
  </article>;
}

export function Storefront() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const home = ['/', '/shop', '/home'].includes(location.pathname.toLowerCase());
  useEffect(() => {
    const target = { '#journal': 'journal', '#affiliate-info': 'affiliate-info' }[location.hash];
    if (target) document.getElementById(target)?.scrollIntoView?.({ block: 'start' });
  }, [location.hash, location.pathname]);
  const [query, setQuery] = useState(params.get('q') || '');
  const [category, setCategory] = useState(params.get('category') || '전체');
  const [brand, setBrand] = useState('');
  const [sort, setSort] = useState('default');
  const [filters, setFilters] = useState(false);
  const [saved, toggle] = useSaved();
  useEffect(() => { const next = new URLSearchParams(location.search); setQuery(next.get('q') || ''); setCategory(next.get('category') || '전체'); }, [location.search]);
  const savedOnly = params.get('saved') === '1';
  const ad = useAdProducts();
  // 등록된 소개 상품이 하나라도 있으면 그것만 쓴다. 예시와 섞으면 어느 것이
  // 진짜인지 화면에서 구분되지 않는다.
  const live = ad.status === 'ready' && ad.products.length > 0;
  const source = live ? ad.products : catalog;
  const categoryList = live ? categoriesOf(ad.products) : categories;
  const products = selectProducts({ query, category, brand, sort, saved, savedOnly, products: source });
  const reset = () => { setQuery(''); setCategory('전체'); setBrand(''); setSort('default'); };

  const trendProduct = (live && ad.products.length > 0) ? ad.products[0] : catalog[4];

  return <main className="am-store">
    <section className="am-collection" aria-labelledby="collection-title">
      <div className="am-section-title">
        <div>
          <span className="am-kicker">{savedOnly ? 'YOUR LITTLE COLLECTION' : home ? 'NEW ARRIVALS · ANTS PICKS' : 'FIND YOUR NEXT FAVORITE'}</span>
          <h2 id="collection-title">{savedOnly ? '모아둔 취향' : home ? '오늘의 신상품 · 트렌드 큐레이션' : '취향대로, 골라보기'}</h2>
        </div>
        <span className="am-result-count" aria-live="polite">{products.length} items</span>
      </div>
      <div className="am-categories" aria-label="상품 카테고리">{categoryList.map(c => <button key={c} aria-pressed={c === category} onClick={() => setCategory(c)}>{c === '전체' && <Grid2X2 size={15} />}{c}</button>)}</div>
      <div className="am-tools"><label className="am-search"><Search size={18} /><input aria-label="상품명 또는 브랜드 검색" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="상품명 또는 브랜드 검색" /></label><button className="am-filter-button" onClick={() => setFilters(!filters)} aria-expanded={filters}><SlidersHorizontal size={17} />필터{brand && ' · 1'}</button><select aria-label="상품 정렬" value={sort} onChange={e => setSort(e.target.value)}><option value="default">최신순 (기본)</option><option value="low">낮은 가격순</option><option value="high">높은 가격순</option></select></div>
      {filters && <div className="am-filter-panel"><label>브랜드 <select value={brand} onChange={e => setBrand(e.target.value)}><option value="">모든 브랜드</option>{[...new Set(source.map(p => p.brand))].map(b => <option key={b}>{b}</option>)}</select></label><button onClick={reset}>필터 초기화</button></div>}
      {products.length ? <div className="am-product-grid">{products.map(product => <ProductCard key={product.id} {...{ product, saved, toggle }} />)}</div> : <div className="am-empty"><Search size={32} /><h3>{savedOnly ? '아직 모아둔 상품이 없어요' : '검색 결과가 없어요'}</h3><p>{savedOnly ? '마음에 드는 상품의 하트를 눌러보세요.' : '다른 검색어를 입력하거나 필터를 바꿔보세요.'}</p><button onClick={reset}>필터 초기화</button></div>}
      {ad.status === 'loading' && <p className="am-preview-note">상품을 불러오는 중입니다…</p>}
      {ad.status === 'error' && <p className="am-preview-note">상품 목록을 불러오지 못했습니다. 아래는 디자인 예시입니다.</p>}
      {ad.status === 'ready' && !live && <p className="am-preview-note">아직 등록된 소개 상품이 없습니다. 아래는 디자인 예시입니다.</p>}
      {live && <p className="am-preview-note">{ad.disclosure || '이 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.'} 파트너스 추적 링크가 없는 상품은 원본 상품 페이지로 연결됩니다.</p>}
    </section>
    {home && <section id="journal" className="am-story">
      <div>
        <span className="am-kicker">TREND NOTES / EDITORIAL PREVIEW</span>
        <h2>{trendProduct.name ? <>{trendProduct.name.slice(0, 24)}<br />트렌드 노트.</> : <>작은 디테일로<br />달라지는 일상.</>}</h2>
        <p>{trendProduct.description || '매일 손이 가는 일상의 디테일. 당신의 다음 발견을 위한 상품 노트를 모았습니다.'}</p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '15px', flexWrap: 'wrap' }}>
          <Link to={`/product/${trendProduct.id}`}>상품 스토리 읽기 <ArrowUpRight size={18} /></Link>
          {trendProduct.affiliateUrl && (
            <a
              href={trendProduct.affiliateUrl}
              target="_blank"
              rel="sponsored noopener noreferrer"
              style={{
                background: '#e1251b',
                color: '#fff',
                padding: '9px 15px',
                borderRadius: '6px',
                fontWeight: '700',
                fontSize: '13px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              쿠팡에서 구매하기 <ArrowUpRight size={15} />
            </a>
          )}
        </div>
      </div>
      <Link to={`/product/${trendProduct.id}`}>
        <img src={trendProduct.image || noProductImage} alt={trendProduct.name} loading="lazy" onError={e => { if (e.target.src === noProductImage) return; e.target.onerror = null; e.target.src = noProductImage; }} />
      </Link>
      <span className="am-story-number" aria-hidden="true">02</span>
    </section>}
    <aside id="affiliate-info" className="am-disclosure"><strong>제휴 및 대가성 안내</strong><p>앤츠마켓은 상품을 직접 판매하지 않고 엄선된 추천 상품을 소개합니다. <strong>이 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.</strong> 구매·결제·배송·교환 및 반품은 연결된 판매처(쿠팡 등)에서 진행됩니다.</p></aside>
  </main>;
}

export function MarketProduct() {
  const { id } = useParams();
  const ad = useAdProducts();
  const [saved, toggle] = useSaved();
  // 목록이 실제 등록분으로 바뀌었으므로 상세도 같은 곳에서 찾아야 한다.
  // 예시만 뒤지면 등록 상품을 눌렀을 때 "찾을 수 없습니다" 가 뜬다.
  const pool = ad.products.length ? ad.products : catalog;
  const product = pool.find(p => String(p.id) === id);
  // 아직 불러오는 중이라면 "없다" 고 단정하지 않는다. 등록 상품을 눌렀을 때
  // 잠깐 '찾을 수 없습니다' 가 떴다 사라지는 깜빡임을 막는다.
  if (!product && ad.status === 'loading') return <main className="am-empty"><h1>불러오는 중입니다…</h1></main>;
  if (!product) return <main className="am-empty"><PackageOpen /><h1>소개를 찾을 수 없습니다</h1><Link to="/ShowItem">상품 소개로 돌아가기</Link></main>;
  const url = coupangLink(product.affiliateUrl);
  const formattedDate = formatDate(product.createdAt);
  const isNew = isNewArrival(product.createdAt);

  return <main className="am-detail am-store">
    <nav className="am-breadcrumb"><Link to="/Shop">발견</Link><span>/</span><Link to={`/ShowItem?category=${product.category}`}>{product.category}</Link><span>/</span>상품 노트</nav>
    <div className="am-detail-grid"><div className="am-detail-image"><img
      src={product.image || noProductImage}
      alt={product.name}
      onError={e => { if (e.target.src === noProductImage) return; e.target.onerror = null; e.target.src = noProductImage; }}
    /></div>
      <article className="am-detail-info">
        <div className="am-detail-kicker-row">
          <span className="am-kicker">{product.brand} / PRODUCT NOTE</span>
          {isNew && <span className="am-badge-new">NEW</span>}
        </div>
        <h1>{product.name}</h1>
        <div className="am-detail-author-box">
          <span>작성자 <strong>{product.author || (product.isAgent ? 'AI 큐레이터' : '관리자')}</strong></span>
          {formattedDate && <span>등록일 <strong>{formattedDate}</strong></span>}
          <span>분야 <strong>{product.category}</strong></span>
        </div>
        <p className="am-detail-category">{product.category} · {url ? '제휴 상품 소개' : '디자인 예시'}</p>
        <p className="am-note-intro">{product.description || '나의 일상에 어울릴지, 천천히 살펴볼 아이템. 디자인을 미리보기 위한 예시 소개입니다.'}</p>
        <div className="am-affiliate-action">{url ? <a className="am-cta" href={url} target="_blank" rel="sponsored noopener noreferrer">쿠팡에서 상품 보기 <ArrowUpRight size={20} /></a> : <p className="am-link-pending">실제 상품 링크 등록 후 소개됩니다.</p>}
          <button className="am-icon" aria-label="상품 소개 저장" aria-pressed={saved.includes(product.id)} onClick={() => toggle(product.id)}><Heart fill={saved.includes(product.id) ? 'currentColor' : 'none'} /></button></div>
        <aside className="am-disclosure"><strong>{url ? '광고 및 대가성 안내 · 쿠팡 파트너스' : '미리보기 안내'}</strong><p>{url ? '이 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.' : '이 상품은 예시 이미지와 정보입니다. 실제 쿠팡 상품 또는 제휴 관계를 의미하지 않습니다.'}</p></aside>
        <details open><summary>구매 전 확인해 주세요</summary><p>앤츠마켓은 상품 소개 매체이며 직접 판매하거나 결제를 받지 않습니다. 가격, 재고, 배송 및 반품 조건은 판매처의 최신 정보를 확인해 주세요.</p></details>
        <Link className="am-back" to="/ShowItem"><ArrowLeft size={16} /> 다른 발견 둘러보기</Link>
      </article></div>
    <section className="am-collection"><div className="am-section-title"><h2>이런 발견도 있어요</h2></div><div className="am-product-grid">{pool.filter(p => p.id !== product.id).slice(0, 4).map(p => <ProductCard key={p.id} product={p} {...{saved, toggle}} />)}</div></section>
  </main>;
}

export function MarketFooter() {
  return <footer className="am-footer">
    <div>
      <Link to="/Shop" className="am-footer-wordmark">ANTS MARKET<span>↗</span></Link>
      <p>좋은 상품, 새로운 소식, 취향의 발견.</p>
      <p style={{ marginTop: '8px', fontSize: '12.5px', color: '#777', lineHeight: '1.6' }}>
        앤츠마켓의 추천 상품 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.
      </p>
    </div>
    <nav aria-label="콘텐츠 안내">
      <Link to="/Shop#journal">트렌드 노트</Link>
      <Link to="/ShowItem">상품 소개</Link>
      <Link to="/support">문의하기</Link>
      <Link to="/Shop#affiliate-info">제휴 안내</Link>
    </nav>
    <div className="am-footer-bottom">
      <span>© {new Date().getFullYear()} ANTS MARKET</span>
      <span style={{ fontWeight: '500' }}>이 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.</span>
      <span>FINDS · STORIES · TRENDS</span>
    </div>
  </footer>;
}
